import { useEffect, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configureApiAuth } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { useThemeInit } from '../hooks/useTheme';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { ToastContainer } from '../components/ui/Toast';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthBridge() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshTokens = useAuthStore((s) => s.refreshTokens);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    configureApiAuth({
      getToken: () => useAuthStore.getState().accessToken,
      refresh: () => useAuthStore.getState().refreshTokens(),
      onFailed: () => useAuthStore.getState().clearAuth(),
    });

    // If we have a refresh token but no access token, try to refresh
    if (refreshToken && !accessToken) {
      refreshTokens().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  return null;
}

function ThemeBridge() {
  useThemeInit();
  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthBridge />
        <ThemeBridge />
        {children}
        <ToastContainer />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
